/**
 * `src/version.ts` — single source of truth for the build's git sha + build time.
 *
 * The values come from Vite `define` (see `vite.config.ts::resolveAppVersion`), which
 * in priority order picks:
 *   1. `GITHUB_SHA` (set by the deploy-frontend-swa.yml workflow's runner)
 *   2. `VITE_GIT_SHA` (explicit override)
 *   3. `git rev-parse --short HEAD` at vite-config time (local builds)
 *   4. `'unknown'` (fallback — git missing, sources copied without .git, etc.)
 *
 * On the wire `__APP_VERSION__` is a short sha (7 chars) — matches the format
 * GitHub Actions tags container images with (`sha-abc1234`) so frontend + backend
 * versions line up visually.
 *
 * Both constants are exposed via:
 *   * `console.info('aletheia frontend …')` on app boot (visible in DevTools immediately)
 *   * `window.__ALETHEIA_VERSION__` (queryable from DevTools console)
 *   * `<VersionPill>` in the global status bar (visible without opening DevTools)
 */

// These two are injected by Vite at build time — see `vite.config.ts::define`.
// The declarations live in `src/vite-env.d.ts`; if anyone changes the names they MUST
// change them in three places (config, declarations, this file).
declare const __APP_VERSION__: string;
declare const __BUILD_TIME__: string;

export const APP_VERSION: string =
  typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'unknown';

export const BUILD_TIME: string =
  typeof __BUILD_TIME__ === 'string' ? __BUILD_TIME__ : 'unknown';

/**
 * Format the build time for display next to the sha. Short form: `YYYY-MM-DDTHH:MM`Z.
 * Falls back to the raw value if parsing fails (defensive — `unknown` etc.).
 */
export function formatBuildTime(): string {
  try {
    const d = new Date(BUILD_TIME);
    if (Number.isNaN(d.getTime())) return BUILD_TIME;
    // ISO truncated to the minute, drop ms + seconds for the pill.
    return d.toISOString().slice(0, 16) + 'Z';
  } catch {
    return BUILD_TIME;
  }
}

/**
 * Attach the version info to `window.__ALETHEIA_VERSION__` and log it once on app
 * boot. Idempotent — safe to call from React StrictMode's double-render. Called by
 * `main.tsx` before React mounts so the log lands before any other noise.
 */
export function installVersionGlobals(): void {
  const w = window as unknown as {
    __ALETHEIA_VERSION__?: { sha: string; buildTime: string };
  };
  if (w.__ALETHEIA_VERSION__ !== undefined) return;
  w.__ALETHEIA_VERSION__ = { sha: APP_VERSION, buildTime: BUILD_TIME };
  // Stand-alone styled console line so it pops in DevTools — easier to spot than the
  // sea of api.client warnings. Single line, no PII.
  // eslint-disable-next-line no-console
  console.info(
    `%caletheia frontend%c ${APP_VERSION} %cbuilt ${formatBuildTime()}`,
    'background: #2a3340; color: #5bb6ef; padding: 2px 6px; border-radius: 3px; font-weight: 600;',
    'color: inherit; font-weight: 600;',
    'color: #6f7882; font-weight: 400;',
  );
}
